import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Navigate, useNavigate } from 'react-router-dom';
import './Dashboard.scss';

interface JobSummary {
  total: number;
  published: number;
  draft: number;
  applications: number;
}

const EmployerDashboard: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [jobSummary, setJobSummary] = useState<JobSummary>({
    total: 0,
    published: 0,
    draft: 0,
    applications: 0
  });

  // Show loading state while auth is being checked
  if (isLoading) {
    return (
      <div className="employer-dashboard">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  // Redirect if not authenticated or not an employer
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (user?.user_type !== 'employer') {
    return <Navigate to="/" replace />;
  }

  const getCompanyName = () => {
    return user.company_name || user.email;
  };

  const handleCreateJob = () => {
    navigate('/jobs/create');
  };

  const handleManageJobs = () => {
    navigate('/jobs');
  };

  const handleViewApplications = () => {
    // Navigate to applications page when implemented
    showToast('Prašymų peržiūra bus pridėta netrukus', 'info');
  };

  const handleViewProfile = () => {
    navigate('/profile');
  };

  return (
    <div className="employer-dashboard">
      <section className="dashboard-header">
        <div className="container">
          <div className="header-content">
            <div className="welcome-section">
              <h1>Sveiki, {getCompanyName()}!</h1>
              <p className="subtitle">Valdykite savo darbo skelbimus ir raskite geriausius kandidatus</p>
            </div>
            
            <div className="quick-actions">
              <button className="action-btn primary" onClick={handleCreateJob}>
                <span className="icon">➕</span>
                Sukurti skelbimą
              </button>
              <button className="action-btn secondary" onClick={handleManageJobs}>
                <span className="icon">📋</span>
                Valdyti skelbimus
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* DASHBOARD STATS */}
      <section className="dashboard-stats">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <h3>{jobSummary.total}</h3>
                <p>Viso skelbimų</p>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon">🌟</div>
              <div className="stat-content">
                <h3>{jobSummary.published}</h3>
                <p>Aktyvūs skelbimai</p>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon">✏️</div>
              <div className="stat-content">
                <h3>{jobSummary.draft}</h3>
                <p>Juodraščiai</p>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon">📧</div>
              <div className="stat-content">
                <h3>{jobSummary.applications}</h3>
                <p>Gauti prašymai</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EMPLOYER FEATURES */}
      <section className="employer-features">
        <div className="container">
          <h2>Darbdavio įrankiai</h2>
          <div className="features-grid">
            <div className="feature-card" onClick={handleManageJobs}>
              <div className="feature-icon">📋</div>
              <h3>Valdyti skelbimus</h3>
              <p>Redaguokite, publikuokite ar ištrinkite esamus skelbimus</p>
              <button className="feature-btn">Valdyti</button>
            </div>
            
            <div className="feature-card" onClick={handleViewApplications}>
              <div className="feature-icon">👥</div>
              <h3>Kandidatų prašymai</h3>
              <p>Peržiūrėkite ir vertinkite gautus darbo prašymus</p>
              <button className="feature-btn">Peržiūrėti</button>
            </div>
            
            <div className="feature-card" onClick={handleViewProfile}>
              <div className="feature-icon">🏢</div>
              <h3>Įmonės profilis</h3>
              <p>Atnaujinkite įmonės informaciją ir kontaktus</p>
              <button className="feature-btn">Redaguoti</button>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">📈</div>
              <h3>Statistika</h3>
              <p>Stebėkite skelbimų peržiūras ir kandidatų aktyvumą</p>
              <button className="feature-btn">Žiūrėti statistiką</button>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">💰</div>
              <h3>Atsiskaitymas</h3>
              <p>Valdykite mokėjimus ir prenumeratos planus</p>
              <button className="feature-btn">Atsiskaitymas</button>
            </div>
          </div>
        </div>
      </section>

      {/* RECENT ACTIVITY */}
      <section className="recent-activity">
        <div className="container">
          <h2>Paskutinė veikla</h2>
          <div className="activity-list">
            <div className="activity-item">
              <div className="activity-icon">📝</div>
              <div className="activity-content">
                <p><strong>Sukurtas naujas skelbimas:</strong> "IT specialistas"</p>
                <span className="activity-time">Prieš 2 valandas</span>
              </div>
            </div>
            
            <div className="activity-item">
              <div className="activity-icon">👤</div>
              <div className="activity-content">
                <p><strong>Naujas prašymas:</strong> "Projektų vadovas" pozicijai</p>
                <span className="activity-time">Prieš 4 valandas</span>
              </div>
            </div>
            
            <div className="activity-item">
              <div className="activity-icon">📊</div>
              <div className="activity-content">
                <p><strong>Skelbimas peržiūrėtas:</strong> "Marketingo specialistas" - 15 kartų</p>
                <span className="activity-time">Prieš 1 dieną</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EmployerDashboard;
